/**
 * @file `GET/PUT /families/{familyId}/study/exam_plans/{personId}` のクライアント。
 *
 * @what: 学習者1人の受験の学習スケジュールの設定 (対象・目標・試験日・科目ごとの進め方) を読み書きする。
 * @why: スケジュールはサーバに保存せず、参考書ルートとこの設定から web が毎回計算する
 *   (設計 plan/docs/study_textbook_route_grid_design.md §3.3)。
 * @failure: GET は設定が無ければ not_found (404) なので `null` を返す。他は ApiError を伝播する。
 */

import { ApiError, apiFetch } from "@/shared/api/client";

export type StudyExamMode = "beginner" | "review";

export const STUDY_EXAM_MODE_LABELS: Record<StudyExamMode, string> = {
  beginner: "初学者",
  review: "学び直し",
};

export interface StudyExamSubjectSetting {
  /** 受験科目として使うか。 */
  enabled: boolean;
  mode: StudyExamMode;
  /** 学び直しのいまの水準 (自己申告) で、無ければ未設定とする。 */
  current_deviation?: number;
  /** 科目ごとの期限 (YYYY-MM-DD) で、無ければ計画全体の期限を使う。 */
  exam_date?: string;
  /** 科目を進める順序 (1から) で、無ければ順序を持たず並行して進む。 */
  order?: number;
  /** 周回数で、無ければ既定 (初学者3 / 学び直し1) を使う。 */
  rounds?: number;
  /** 週に使える分を持つ。 */
  weekly_minutes?: number;
  /** 使わない区分 (参考書ルートの tracks[].id)。無ければ全部の区分を使う。 */
  excluded_tracks?: string[];
}

export interface StudyExamPlanInput {
  /** 受験の対象 (参考書ルートの target)。 */
  target: string;
  goal_school?: string;
  goal_deviation?: number;
  /** YYYY-MM-DD */
  exam_date?: string;
  /** YYYY-MM-DD で、無ければ今日を起点にする。 */
  start_date?: string;
  subjects: Record<string, StudyExamSubjectSetting>;
}

export interface StudyExamPlan extends StudyExamPlanInput {
  schema_v: number;
  learner_person_id: string;
  updated_by_person_id?: string;
  created_at: string;
  updated_at: string;
}

function planPath(familyId: string, personId: string): string {
  return `/families/${encodeURIComponent(familyId)}/study/exam_plans/${encodeURIComponent(personId)}`;
}

export async function fetchExamPlan(
  familyId: string,
  personId: string,
  signal?: AbortSignal,
): Promise<StudyExamPlan | null> {
  try {
    return await apiFetch<StudyExamPlan>(planPath(familyId, personId), signal ? { signal } : {});
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return null;
    throw err;
  }
}

export function putExamPlan(
  familyId: string,
  personId: string,
  input: StudyExamPlanInput,
): Promise<StudyExamPlan> {
  return apiFetch<StudyExamPlan>(planPath(familyId, personId), { method: "PUT", body: input });
}
